/**
 * The "lower price option" flag on a quote, for Kono operators: a vendor
 * answered after the quote was generated with a better price (or an item
 * the quote could not cover). Links to the RFQ, where the option is acted on.
 */
import { Link } from "react-router-dom";
import { QuoteBetterPriceFlag } from "./api";

const money = (value: number, currency: string): string => {
  const amount = value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency === "USD" ? `$${amount}` : `${amount} ${currency}`;
};

const TONE: Record<QuoteBetterPriceFlag["status"], string> = {
  open: "bg-amber-100 text-amber-900 border-amber-300",
  revised: "bg-green-100 text-green-900 border-green-300",
  dismissed: "bg-gray-100 text-gray-600 border-gray-300",
};

export const betterPriceLabel = (flag: QuoteBetterPriceFlag): string => {
  if (flag.status === "revised") return `Lower price applied${flag.revisedQuoteReference ? `: ${flag.revisedQuoteReference}` : ""}`;
  if (flag.status === "dismissed") return "Lower price option dismissed";
  return flag.savings > 0 ? `Lower price option: ${money(flag.savings, flag.currency || "USD")} less` : `Lower price option: ${flag.lines} line${flag.lines === 1 ? "" : "s"} now available`;
};

export const BetterPriceBadge = ({ flag, className = "" }: { flag: QuoteBetterPriceFlag | null | undefined; className?: string }) => {
  if (!flag) return null;
  const title = `${flag.vendors.join(", ") || "A vendor"} answered after this quote was generated, ${new Date(flag.detectedAt).toLocaleString()}. Open the RFQ to thank the vendor, write the customer email and approve the new version.`;
  return (
    <Link
      to={`/rfq?id=${encodeURIComponent(flag.rfqId)}`}
      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-medium hover:underline ${TONE[flag.status]} ${className}`}
      title={title}
      data-testid="better-price-badge"
    >
      {betterPriceLabel(flag)}
    </Link>
  );
};
